import { createAsyncThunk } from '@reduxjs/toolkit';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types.d';
import { AppDispatch } from '@store/types';
import type { CropImageRequestDto } from '@api/generated/models/cropImageRequestDto';
import { Alert } from '@helpers';
import { backendCall, createApiCall } from '@reducers/utils';
import { limitMinimumSize, smallImageHeight } from '@components/imageHandling/common';
import { scale } from '@components/frame';
import { updateImageDocumentInDict } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';

interface CropImageDocumentPayload {
  imageDocument: ImageDocumentStoreItem & { angle?: number };
}

export const cropImageDocument = createAsyncThunk<void, CropImageDocumentPayload, { dispatch: AppDispatch }>(
  'imageHandlingV2/cropImageDocument',
  async ({ imageDocument }, { dispatch }) => {
    const { x = 0, y = 0, widthOriginal, heightOriginal, height = heightOriginal, width = widthOriginal } = imageDocument;

    const imageToUpdate = { ...imageDocument };

    const angle = imageDocument.angle;
    delete imageDocument.angle;
    delete imageDocument.croppedImageUri;
    // we don't need these fields in store

    imageToUpdate.width = width;
    imageToUpdate.height = height;
    imageToUpdate.x = x;
    imageToUpdate.y = y;
    imageToUpdate.isCropped = true;
    imageToUpdate.customRotate = { ...imageToUpdate.customRotate, [imageToUpdate.pageNumber!]: 0 };
    imageToUpdate.activeImageWidth = limitMinimumSize((scale(smallImageHeight) * width) / height, scale);

    dispatch(updateImageDocumentInDict({ documentId: imageDocument.documentId!, updatedImageDocument: imageToUpdate }));

    // Check for invalid dimensions that could cause division by zero
    if (!widthOriginal || !heightOriginal || widthOriginal <= 0 || heightOriginal <= 0) {
      throw new Error(`Invalid image dimensions: width=${widthOriginal}, height=${heightOriginal}`);
    }

    const toTwo = (n: number) => Number(Number(n).toFixed(2));
    const shouldSwapDimensions = angle === 90 || angle === 270;

    const backendData: CropImageRequestDto = {
      documentId: imageDocument.documentId,
      imageCropSettings: {
        rotationAngle: angle,
        pageNumber: imageToUpdate.pageNumber ?? undefined,
        ...(!shouldSwapDimensions
          ? {
              upperLeftXPosition: toTwo(x / widthOriginal),
              lowerRightXPosition: toTwo((x + width) / widthOriginal),
              upperLeftYPosition: toTwo(y / heightOriginal),
              lowerRightYPosition: toTwo((y + height) / heightOriginal),
            }
          : {
            upperLeftXPosition: toTwo(x / heightOriginal),
            lowerRightXPosition: toTwo((x + width) / heightOriginal),
            upperLeftYPosition: toTwo(y / widthOriginal),
            lowerRightYPosition: toTwo((y + height) / widthOriginal),
            }),
      },
    };

    await dispatch(
      backendCall(
        createApiCall((api) => api.imageHandling.cropImage, {
          params: backendData,
          onSuccess: () => {
            trackEvent(TRACKING_EVENTS.DOCUMENT, { action: 'cropped' });
          },
          onError: (error) => {
            console.error(error);
            Alert({ title: 'Error during crop image. Please, try again.' });
          },
        })
      )
    );
  }
);
